import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Row from '@amzn/meridian/row';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import type { TableCellAlignmentHorizontal } from '@amzn/meridian/table/table-cell';
import Link from '@amzn/meridian/link';
import { FormattedMessage } from 'react-intl';
import { getDocumentURL } from '../handler/ParentDataHandler';
import { getAssociateMetric, AssociateMetric } from './data/associate-api';
import { getAliasFromLoginId, getRefreshInterval, buildAssociateSearchLink } from './util';
import RefreshTimer from './RefreshTimer';

export interface AssociatePerformanceProps {
  /** Active-state filter shared from AssociateFilter (empty = show all). */
  states?: string[];
}

interface SortPref {
  column: keyof AssociateMetric;
  direction: 'ascending' | 'descending';
}

/** Right-align numeric columns. Meridian's alignment union uses logical "end" (right in LTR). */
const ALIGN_RIGHT: TableCellAlignmentHorizontal = 'end';

/**
 * AssociatePerformance — sortable associate roster (React + Meridian, ported from v1).
 * Loads on mount + polls every 60s (paused when the tab is hidden), sorts, filters by the
 * shared active-state, and deep-links each alias to the Search app. Design-system continuity:
 * same Meridian Table/Link/Row as v1.
 */
const AssociatePerformance: React.FC<AssociatePerformanceProps> = ({ states = [] }) => {
  const [rows, setRows] = useState<AssociateMetric[]>([]);
  const [baseURL, setBaseURL] = useState<string>('');
  const [sortPref, setSortPref] = useState<SortPref>({ column: 'alias', direction: 'ascending' });

  const getData = useCallback(async () => {
    const [metrics, url] = await Promise.all([getAssociateMetric(), getDocumentURL()]);
    setRows(metrics);
    if (typeof url === 'string') setBaseURL(url);
  }, []);

  useEffect(() => {
    getData();
    const interval = setInterval(() => {
      if (!document.hidden) getData();
    }, getRefreshInterval());
    return () => clearInterval(interval);
  }, [getData]);

  const onSort = ({ sortColumn, sortDirection }: { sortColumn: keyof AssociateMetric; sortDirection: SortPref['direction'] }) => {
    setSortPref({ column: sortColumn, direction: sortDirection });
  };

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      if (a[sortPref.column] < b[sortPref.column]) return sortPref.direction === 'ascending' ? -1 : 1;
      if (a[sortPref.column] > b[sortPref.column]) return sortPref.direction === 'ascending' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortPref]);

  return (
    <div>
      <Row widths="100%" alignmentVertical="top">
        <div>
          <RefreshTimer deps={[rows]} />
          <br />
          <div style={{ overflowX: 'auto', width: '100%' }}>
          <Table
            headerRows={1}
            spacing="small"
            sortColumn={sortPref.column}
            sortDirection={sortPref.direction}
            onSort={onSort}
            fixHeaderRows
            headerColumns={1}
            stickyHeaderColumn
          >
            <TableRow>
              <TableCell sortColumn="alias"><FormattedMessage id="associate.associate" defaultMessage="Associate" /></TableCell>
              <TableCell sortColumn="PPH" alignmentHorizontal={ALIGN_RIGHT}><FormattedMessage id="associate.pph" defaultMessage="PPH" /></TableCell>
              <TableCell sortColumn="hourSpent" alignmentHorizontal={ALIGN_RIGHT}><FormattedMessage id="associate.hours" defaultMessage="Hours" /></TableCell>
              <TableCell sortColumn="packageHandled" alignmentHorizontal={ALIGN_RIGHT}><FormattedMessage id="associate.total_packages" defaultMessage="Total Packages" /></TableCell>
              <TableCell sortColumn="active"><FormattedMessage id="associate.active" defaultMessage="Active" /></TableCell>
              <TableCell sortColumn="location"><FormattedMessage id="associate.location" defaultMessage="Location" /></TableCell>
            </TableRow>
            {sorted
              .filter((a) => states.length === 0 || states.includes(a.active))
              .map((a) => (
                <TableRow key={a.alias}>
                  <TableCell>
                    <Link href={buildAssociateSearchLink(baseURL, a.email)} type="secondary" target="_blank" rel="noreferrer noopener">
                      {getAliasFromLoginId(a.alias)}
                    </Link>
                  </TableCell>
                  <TableCell alignmentHorizontal={ALIGN_RIGHT}>{a.PPH}</TableCell>
                  <TableCell alignmentHorizontal={ALIGN_RIGHT}>{a.hourSpent}</TableCell>
                  <TableCell alignmentHorizontal={ALIGN_RIGHT}>{a.packageHandled}</TableCell>
                  <TableCell>{a.active}</TableCell>
                  <TableCell>{a.location}</TableCell>
                </TableRow>
              ))}
          </Table>
          </div>
        </div>
      </Row>
    </div>
  );
};

export default AssociatePerformance;
